import type { SelectionEntry } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import type {
  NormalizedSearchRequest,
  ParsedSearchRequest,
  SearchRequestDefaults,
} from 'Models/uiConfig/requestTypes';
import uiConfig from 'Models/uiConfig/uiConfig';
import isTruthy from 'Utils/isTruthy';

import { normalizeParsedSearchRequest } from './requestNormalization';
import { uriDecode, uriDecodeTerm } from './uriEncoding';

const { defaultSort, defaultPageSize } = requestConfig;

const requestDefaults: SearchRequestDefaults = {
  catalog: '',
  filterQuery: '',
  pageNumber: 0,
  pageSize: defaultPageSize,
  query: '',
  selection: [],
  sort: defaultSort,
};

export const defaultRequest = normalizeParsedSearchRequest({}, requestDefaults);

type UriSearchHash = { search?: string; hash?: string };

export default function getRequestFromUri(location: UriSearchHash): NormalizedSearchRequest {
  const parser = uiConfig.customUriParser;

  const request =
    parser?.parsingNeeded?.(uiConfig.pageType) && typeof parser.parse === 'function'
      ? parser.parse(uiConfig.pageType)
      : doDefaultParsing(location);

  return normalizeParsedSearchRequest(request, requestDefaults);
}

// The URI parser only writes to a fixed subset of ParsedSearchRequest. Keeping
// the accumulator narrow makes unknown keys an error instead of silently
// landing in the request object.
type UriParsedRequest = Pick<
  ParsedSearchRequest,
  'catalog' | 'filterQuery' | 'pageNumber' | 'pageSize' | 'query' | 'selection' | 'sort'
>;

function doDefaultParsing(location: UriSearchHash): UriParsedRequest {
  const { rawSelection, params } = parseUri(location);
  let selectionFromParams = '';

  const pageNumber = requestConfig.pageUriParam.toLowerCase();

  const request = Object.entries(params).reduce<UriParsedRequest>((req, [name, rawValue]) => {
    switch (name) {
      case 'facet':
        selectionFromParams = rawValue;
        break;
      case 'filterquery':
        req.filterQuery = uriDecode(rawValue);
        break;
      case 'sort':
        req.sort = decodeURIComponent(rawValue);
        break;
      case pageNumber:
      case 'page':
      case 'pagenumber': {
        const parsed = Number(rawValue);
        if (Number.isFinite(parsed)) {
          req.pageNumber = parsed - 1;
        }
        break;
      }
      case 'pagesize':
      case 'showed': {
        if (rawValue === 'all') {
          req.pageSize = 'all';
        } else {
          const parsed = Number(rawValue);
          if (Number.isFinite(parsed)) {
            req.pageSize = parsed;
          }
        }
        break;
      }
      case 'query':
        req.query = uriDecode(rawValue);
        break;
      case 'catalog':
        req.catalog = uriDecode(rawValue);
        break;
    }
    return req;
  }, {});

  request.selection = prepareSelection([rawSelection, selectionFromParams].filter(isTruthy).join('/'));
  return request;
}

function parseUri({ search, hash }: UriSearchHash) {
  function parse(s: string) {
    const split = s.replace(/[?#]/, '').split('&');

    const rawSelection = looksLikeSelection(split[0] || '');
    if (rawSelection) {
      split.shift();
    }
    const params = split.reduce((p: Record<string, string>, part) => {
      const [key, value] = part.split('=');
      if (key) {
        p[key.toLowerCase()] = value;
      }
      return p;
    }, {});
    return [rawSelection, params] as const;
  }
  const [sRawSelection, sParams] = parse(search || '');
  const [hRawSelection, hParams] = parse(hash || '');
  const rawSelection = sRawSelection || hRawSelection || rawSelectionFromHash(hParams);

  return {
    rawSelection,
    params: { ...sParams, ...hParams },
  };
}

function looksLikeSelection(param: string) {
  if (param.includes('/') && !param.includes('=')) {
    return param;
  }
  const decoded = decodeURIComponent(param);
  if (decoded.includes('/') && (!decoded.includes('=') || decoded.endsWith('='))) {
    return decoded.replace('=', '');
  }
  return '';
}

function rawSelectionFromHash(hParams: Record<string, string>) {
  const facets: string[] = [];
  for (let i = 0; ; i++) {
    const field = hParams[`fld${i}`];
    const sel = hParams[`sel${i}`];
    if (field && sel) {
      facets.push(`${field}/${sel}`);
    } else {
      break;
    }
  }
  return facets.join('/');
}

function prepareSelection(raw: string) {
  const selection: SelectionEntry[] = [];

  const split = raw.split('/').filter(isTruthy);
  for (let i = 0; i < split.length; i += 2) {
    const fieldRaw = split[i];
    const termRaw = split[i + 1];
    if (typeof fieldRaw !== 'string' || typeof termRaw !== 'string') {
      continue;
    }
    const field = uriDecode(fieldRaw);
    const term = uriDecodeTerm(termRaw);
    if (!field || !term) {
      continue;
    }

    selection.push({ field, term });
  }
  return selection;
}
